// Fee slips and invoices filed from email by the intake job (api/cron/email-intake.js),
// shaped as rows for the fee slip screen.
//
// That screen is organised clerk-first: a row is only ever seen inside its clerk's group.
// An emailed item usually arrives with no clerk, so each kind gets a group of its own until
// someone sorts it - and a fee slip given a clerk moves into that clerk's group alongside
// the statement rows.
//
// An item goes straight to its clerk's group when the email says whose it is: the clerk's
// accounts address, the clerk named in the subject, or the invoice number (src/lib/
// clerkMatching.js). A clerk chosen by hand always wins.

import { clerkFromEmail, clientFromSubject, counselFromSubject } from "./clerkMatching.js";
import { clientFromFileName } from "./invoiceClient.js";

export const EMAIL_INTAKE_BRANDS = ["lacw", "vals", "eas"];

export const EMAIL_KIND_LABELS = {
  fee_slip: "Fee slip",
  disbursement: "Disbursement",
  check_type: "Check type",
};

export const EMAIL_GROUP_NAMES = {
  fee_slip: "From email – fee slips",
  disbursement: "Disbursements",
  check_type: "From email – check type",
};

export const EMAIL_ONLY_GROUPS = Object.values(EMAIL_GROUP_NAMES);

export function emailIntakeGroup(item = {}) {
  const clerk = String(item.clerk_name || "").trim();
  if (item.kind === "fee_slip" && clerk) return clerk;
  // A disbursement stays with disbursements even from a clerk; anything else a clerk's
  // accounts team sends is one of their barristers' invoices.
  const matched = item.kind !== "disbursement" ? clerkFromEmail(item) : null;
  if (matched) return matched.name;
  return EMAIL_GROUP_NAMES[item.kind] || EMAIL_GROUP_NAMES.check_type;
}

// The intake names an untagged item after its subject. When that is still the name, and the
// subject says whose matter it is, the client's name is shown instead. A name typed in by
// hand is never replaced.
//
// Best first, once the stored name is shown to be the subject's and not typed: the client
// on the fee slip's MATTER: line (pdf_client, read by the intake), then the fee slip's file
// name ("HOOD, Baymos v Informant ... Invoice 00949042 ...pdf"), then the subject's "in the
// matter of" (src/lib/invoiceClient.js, Emma 26 Oct 2026).
export function emailClientName(item = {}) {
  const stored = String(item.client_name || "").trim();
  const subject = String(item.subject || "");
  const typed = stored && !subject.toLowerCase().includes(stored.toLowerCase());
  if (typed) return stored;
  return String(item.pdf_client || "").trim()
    || clientFromFileName(item.file_name)
    || clientFromSubject(subject)
    || stored;
}

// "Jane Smith <jane@lacw.org.au>" -> "Jane Smith"
export function senderName(sender = "") {
  const text = String(sender || "").trim();
  const angled = text.match(/^\s*"?([^"<]*?)"?\s*<([^>]+)>\s*$/);
  if (angled) return angled[1].trim() || angled[2].trim();
  return text;
}

// The note on a row filed by what the email says, so a wrong guess is easy to spot and move.
export const CLERK_FROM_NOTES = {
  sender: { label: "Clerk from sender", title: "Put in this clerk's list because it came from the clerk's accounts address" },
  subject: { label: "Clerk from subject", title: "Put in this clerk's list because the subject or file name names the clerk" },
  invoice: { label: "Clerk from invoice no.", title: "Put in this clerk's list because the invoice number is in this clerk's run of numbers" },
};

export function toFeeSlipRow(item = {}) {
  const matchedClerk = !String(item.clerk_name || "").trim() && item.kind !== "disbursement"
    ? clerkFromEmail(item)
    : null;
  return {
    id: `email-${item.id}`,
    sourceId: item.id,
    source: "email",
    emailKind: item.kind,
    clientName: emailClientName(item) || "Client not yet named",
    counselName: counselFromSubject(item.subject) || senderName(item.sender) || "Emailed",
    clerkName: emailIntakeGroup(item),
    invoiceName: item.file_name || "No document attached",
    invoiceSize: Number(item.file_size) || 0,
    invoiceBase64: null,
    invoiceAmount: item.amount != null && item.amount !== "" ? String(item.amount) : "0.00",
    status: item.status || "not_paid",
    notes: item.notes || "",
    uploadedAt: String(item.received_at || item.created_at || ""),
    // The latest email carrying this invoice number, re-sent copies included (api/email-intake.js).
    lastSentAt: String(item.last_sent_at || ""),
    documentUrl: item.file_path ? `/api/files?path=${encodeURIComponent(item.file_path)}` : "",
    fileNote: item.file_note || "",
    subject: item.subject || "",
    tagged: Boolean(item.tagged),
    invoiceRef: item.invoice_ref || "",
    possibleDuplicate: Boolean(item.possible_duplicate),
    // Read off the attached PDF by the intake, not typed by a person: shown so it is checked.
    amountFromPdf: item.amount_source === "pdf" && item.amount != null,
    clerkFrom: matchedClerk?.from || "",
    clerkFromSender: matchedClerk?.from === "sender",
    dismissedAt: item.dismissed_at || "",
    dismissedBy: item.dismissed_by || "",
    rawEmail: item,
  };
}

export async function fetchEmailIntake(brand, fetchImpl = globalThis.fetch) {
  const response = await fetchImpl(`/api/email-intake?brand=${encodeURIComponent(brand)}`, {
    credentials: "include",
    cache: "no-store",
  });
  if (!response.ok) throw new Error(`Emailed items returned ${response.status}`);
  const payload = await response.json();
  return Array.isArray(payload) ? payload : [];
}

// The items a person dismissed (administrators only), for the fee slip search.
export async function fetchDismissedEmailIntake(brand, fetchImpl = globalThis.fetch) {
  const response = await fetchImpl(`/api/email-intake?brand=${encodeURIComponent(brand)}&dismissed=1`, {
    credentials: "include",
    cache: "no-store",
  });
  if (!response.ok) throw new Error(`Dismissed items returned ${response.status}`);
  const payload = await response.json();
  return Array.isArray(payload) ? payload : [];
}

export async function updateEmailIntake(id, patch, fetchImpl = globalThis.fetch) {
  const response = await fetchImpl(`/api/email-intake?id=${encodeURIComponent(id)}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.error || `The emailed item could not be saved (${response.status})`);
  return payload;
}
